import { useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../auth/UserContext';
import { getToken } from '../auth/api';
import { log } from '../utils/commonUtils';
import { makeServerSideEventConnection } from './makeServerSideEventConnection';
import type { ServerSideEventConnection, ServerSideEventConnectionOptions } from './makeServerSideEventConnection';
import type { ServerSideEventError } from './ServerSideEventError';
import type { EventType, ServerSideEventContextValue } from './types';

type LegacyEventState = { connection: ServerSideEventConnection; event: EventType | null; queue: EventType[] };

const readServerSideEventToken = () => {
    const token = getToken()?.token;
    return token;
};
const reportServerSideEventError = (error: ServerSideEventError) => {
    log(error.message, error.cause);
};

export const useServerSideEventContext = (): ServerSideEventContextValue => {
    const { activeOrganizationAccount, isAuthenticated } = useContext(UserContext);
    const connection = useMemo(() => {
        const options: ServerSideEventConnectionOptions = {
            ownerId: activeOrganizationAccount,
            authenticated: isAuthenticated,
            readToken: readServerSideEventToken,
            reportError: reportServerSideEventError,
            requestMode: window.DEBUG ? 'no-cors' : 'same-origin'
        };
        const eventConnection = makeServerSideEventConnection(options);
        return eventConnection;
    }, [activeOrganizationAccount, isAuthenticated]);
    const [legacyState, setLegacyState] = useState<LegacyEventState>({ connection, event: null, queue: [] });

    useEffect(() => {
        // The identity guard also hides old-scope messages before this effect runs.
        setLegacyState((previous) => {
            const nextState = previous.connection === connection ? previous : { connection, event: null, queue: [] };
            return nextState;
        });
        const unsubscribe = connection.subscribe((receivedEvent) => {
            setLegacyState((previous) => {
                const nextState = {
                    connection,
                    event: previous.connection === connection ? previous.event : null,
                    queue: [...(previous.connection === connection ? previous.queue : []), receivedEvent]
                };
                return nextState;
            });
        });
        connection.connect();
        const cleanup = () => {
            unsubscribe();
            connection.disconnect();
        };
        return cleanup;
    }, [connection]);

    useEffect(() => {
        if (legacyState.connection !== connection || !legacyState.queue.length) return;
        setLegacyState((previous) => {
            if (previous.connection !== connection || !previous.queue.length) return previous;
            const nextState = { ...previous, event: previous.queue[0], queue: previous.queue.slice(1) };
            return nextState;
        });
    }, [connection, legacyState]);

    const event = legacyState.connection === connection ? legacyState.event : null;
    const contextValue = useMemo(() => {
        const value = { event, subscribe: connection.subscribe, getEventSource: connection.getEventSource };
        return value;
    }, [connection, event]);
    return contextValue;
};
